import { usePage } from "@inertiajs/react"

import type { Auth, Session, SharedData, User } from "@/types"

interface UseAuthReturn {
  auth: Auth | null
  user: User | null
  isAuthenticated: boolean
  session: Pick<Session, "id"> | null
}

/**
 * Hook for safely accessing authentication state with automatic null checking.
 * Provides a consistent API similar to Devise's current_user pattern.
 *
 * @example
 * const { user, isAuthenticated } = useAuth()
 * if (isAuthenticated) {
 *   console.log(`Welcome ${user.name}`)
 * }
 */
export function useAuth(): UseAuthReturn {
  const page = usePage<SharedData>()
  // Safe access with optional chaining and nullish coalescing
  const auth = page.props?.auth ?? null

  return {
    auth,
    user: auth?.user ?? null,
    session: auth?.session ?? null,
    isAuthenticated: Boolean(auth?.user),
  }
}

/**
 * Hook that returns only the current user or null.
 * Useful when you only need user data and not the full auth context.
 *
 * @example
 * const user = useCurrentUser()
 * if (user) {
 *   return <UserProfile user={user} />
 * }
 */
export function useCurrentUser(): User | null {
  return useAuth().user
}

/**
 * Hook that returns a boolean indicating if the user is authenticated.
 * Useful for conditional rendering based on auth state.
 *
 * @example
 * const isAuthenticated = useIsAuthenticated()
 * return isAuthenticated ? <Dashboard /> : <LoginPrompt />
 */
export function useIsAuthenticated(): boolean {
  return useAuth().isAuthenticated
}

/**
 * Hook that returns the current session or null.
 * Rarely needed directly, but available for advanced use cases.
 */
export function useCurrentSession(): Pick<Session, "id"> | null {
  return useAuth().session
}
